import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import followUpsService from '../services/followUps.service';
import type { FollowUpItem, FollowUpItemType } from '../services/followUps.service';
import { useToast } from './ToastContext';
import { normalizeHex } from '../utils/followUpColors';

export type { FollowUpItem, FollowUpItemType };

interface FollowUpContextType {
  /** Newest first. */
  items: FollowUpItem[];
  loading: boolean;
  isFollowed: (type: FollowUpItemType, id: string) => boolean;
  follow: (type: FollowUpItemType, id: string, label: string) => Promise<void>;
  unfollow: (type: FollowUpItemType, id: string) => Promise<void>;
  setNote: (id: number, note: string | null) => Promise<void>;
  setColor: (id: number, color: string | null) => Promise<void>;
  /** Commit a drag. `orderedIds` is top to bottom. */
  reorderItems: (orderedIds: number[]) => Promise<void>;
}

const FollowUpContext = createContext<FollowUpContextType | null>(null);

const key = (type: FollowUpItemType, id: string) => `${type}::${String(id).trim()}`;

/**
 * Optimistic rows carry a negative id so they can never collide with a real
 * one, and so a stray delete on a not-yet-saved row is easy to spot.
 */
let tempIdCounter = 0;
const nextTempId = () => --tempIdCounter;

export const FollowUpProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [items, setItems] = useState<FollowUpItem[]>([]);
  const [loading, setLoading] = useState(true);
  const { showToast } = useToast();

  // Reads the freshest list from inside a callback without making every
  // callback depend on `items` (which would re-create the whole context value
  // on each mutation and re-render every bookmark toggle on the page).
  const itemsRef = useRef<FollowUpItem[]>(items);
  itemsRef.current = items;

  useEffect(() => {
    let cancelled = false;
    followUpsService
      .list()
      .then((rows) => { if (!cancelled) setItems(rows); })
      .catch(() => {
        // Deliberately silent. The panel is mounted on EVERY authenticated
        // page, so a backend blip must leave an empty panel rather than greet
        // the user with an error toast on a page they never asked it about.
        if (!cancelled) setItems([]);
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const isFollowed = useCallback(
    (type: FollowUpItemType, id: string) =>
      itemsRef.current.some((i) => key(i.item_type, i.item_id) === key(type, id)),
    // itemsRef is a ref, but `items` has to be in the deps: this returns a
    // BOOLEAN, so a consumer only re-renders when the identity changes.
    [items],
  );

  const follow = useCallback(
    async (type: FollowUpItemType, id: string, label: string) => {
      const itemId = String(id).trim();
      if (!itemId) return;
      // A detail page that re-mounts must not fire a second write.
      if (itemsRef.current.some((i) => key(i.item_type, i.item_id) === key(type, itemId))) return;

      const tempId = nextTempId();
      const now = new Date().toISOString();
      const optimistic: FollowUpItem = {
        id: tempId,
        item_type: type,
        item_id: itemId,
        label,
        note: null,
        color: null,
        // null = never hand-placed, which is what the server sorts to the top.
        sort_order: null,
        created_at: now,
        updated_at: now,
      };
      setItems((prev) => [optimistic, ...prev]);

      try {
        const saved = await followUpsService.add(type, itemId, label);
        // Match on the temp id, not on (type, id): the server row may already
        // have existed with a note attached, and swapping in place keeps the
        // row where the user just watched it appear.
        setItems((prev) => prev.map((i) => (i.id === tempId ? saved : i)));
      } catch {
        setItems((prev) => prev.filter((i) => i.id !== tempId));
        showToast({ message: 'Could not add that to Follow Ups.', type: 'error' });
      }
    },
    [showToast],
  );

  const unfollow = useCallback(
    async (type: FollowUpItemType, id: string) => {
      const target = itemsRef.current.find((i) => key(i.item_type, i.item_id) === key(type, id));
      if (!target) return;
      const index = itemsRef.current.indexOf(target);

      setItems((prev) => prev.filter((i) => i.id !== target.id));

      try {
        await followUpsService.remove(target.id);
      } catch {
        // Restore at the original index, not at the top: a failed removal must
        // not silently reorder the panel.
        setItems((prev) => {
          const next = [...prev];
          next.splice(Math.min(index, next.length), 0, target);
          return next;
        });
        showToast({ message: 'Could not remove that from Follow Ups.', type: 'error' });
      }
    },
    [showToast],
  );

  const setNote = useCallback(
    async (id: number, note: string | null) => {
      const target = itemsRef.current.find((i) => i.id === id);
      if (!target) return;
      const previous = target.note;
      const trimmed = note?.trim() ? note.trim() : null;

      setItems((prev) => prev.map((i) => (i.id === id ? { ...i, note: trimmed } : i)));

      try {
        const saved = await followUpsService.update(id, { note: trimmed });
        setItems((prev) => prev.map((i) => (i.id === id ? saved : i)));
      } catch {
        setItems((prev) => prev.map((i) => (i.id === id ? { ...i, note: previous } : i)));
        showToast({ message: 'Could not save that note.', type: 'error' });
      }
    },
    [showToast],
  );

  const setColor = useCallback(
    async (id: number, color: string | null) => {
      const target = itemsRef.current.find((i) => i.id === id);
      if (!target) return;
      const previous = target.color;
      const next = normalizeHex(color);

      setItems((prev) => prev.map((i) => (i.id === id ? { ...i, color: next } : i)));

      try {
        // Patches ONLY `color`: the pencil popover edits note and colour
        // independently, so sending both would blank a note still being typed.
        const saved = await followUpsService.update(id, { color: next });
        setItems((prev) => prev.map((i) => (i.id === id ? saved : i)));
      } catch {
        setItems((prev) => prev.map((i) => (i.id === id ? { ...i, color: previous } : i)));
        showToast({ message: 'Could not save that colour.', type: 'error' });
      }
    },
    [showToast],
  );

  const reorderItems = useCallback(
    async (orderedIds: number[]) => {
      if (!orderedIds.length) return;
      const previous = itemsRef.current;
      const byId = new Map(previous.map((i) => [i.id, i]));
      const next = orderedIds.map((id) => byId.get(id)).filter((i): i is FollowUpItem => !!i);
      if (!next.length) return;

      setItems(next);

      try {
        // Adopt the server's list rather than keeping the local guess: it may
        // carry a row this client never had (bookmarked in another tab), which
        // the server places at the top as un-ordered.
        const saved = await followUpsService.reorder(orderedIds);
        if (saved.length) setItems(saved);
      } catch {
        setItems(previous);
        showToast({ message: 'Could not save that order.', type: 'error' });
      }
    },
    [showToast],
  );

  return (
    <FollowUpContext.Provider
      value={{ items, loading, isFollowed, follow, unfollow, setNote, setColor, reorderItems }}
    >
      {children}
    </FollowUpContext.Provider>
  );
};

/**
 * Inert stand-in for consumers rendered outside the provider. Every detail page
 * carries a bookmark toggle and their tests do not all wrap in
 * FollowUpProvider, so this must never throw. Mirrors ViewedProsContext.
 */
const NOOP_FOLLOW_UPS: FollowUpContextType = {
  items: [],
  loading: false,
  isFollowed: () => false,
  follow: async () => {},
  unfollow: async () => {},
  setNote: async () => {},
  setColor: async () => {},
  reorderItems: async () => {},
};

export const useFollowUps = (): FollowUpContextType =>
  useContext(FollowUpContext) ?? NOOP_FOLLOW_UPS;
